import {
  type TersaModel,
  type TersaProvider,
} from '../providers';
import type { ExtendedImageModel } from './base';
import type { ImagePluginId } from './plugins/registry';
import type { InlineMediaConfig } from '../inline-attachments';
import { mergeInlineMedia } from '../inline-attachments';

/**
 * 根据当前选中的尺寸和模型约束，返回有效的分辨率列表。
 * 极端比例（如 1x8）可能只允许低分辨率，避免像素过大。
 */
export function getEffectiveResolutions(
  resolutions: string[] | undefined,
  size: string | undefined,
  constraints: Record<string, string[]> | undefined,
): string[] {
  if (!resolutions || resolutions.length === 0) return [];
  if (!size || !constraints) return resolutions;
  return constraints[size] ?? resolutions;
}

export type BackupResolverContext = {
  size?: string;
  resolution?: string;
};

export type PrimaryModelOption = ExtendedImageModel | ((ctx: BackupResolverContext) => ExtendedImageModel);

export type BackupList = ExtendedImageModel[] | ((ctx: BackupResolverContext) => ExtendedImageModel[]);

function normalizeCtx(ctx: BackupResolverContext): BackupResolverContext {
  if (!ctx.resolution || ctx.resolution === ctx.resolution.toUpperCase()) return ctx;
  return { ...ctx, resolution: ctx.resolution.toUpperCase() };
}

export function resolvePrimaryModel(
  raw: PrimaryModelOption | undefined,
  fallback: ExtendedImageModel | undefined,
  ctx: BackupResolverContext,
): ExtendedImageModel | undefined {
  if (!raw) return fallback;
  return typeof raw === 'function' ? raw(normalizeCtx(ctx)) : raw;
}

export function resolveBackups(
  raw: BackupList | undefined,
  ctx: BackupResolverContext,
): ExtendedImageModel[] {
  if (!raw) return [];
  return typeof raw === 'function' ? raw(normalizeCtx(ctx)) : raw;
}

/**
 * 嵌套声明式路由。第一层 resolution，第二层 size，'other' 为兜底。
 * key 支持逗号分隔。适用于主通道 (T=ExtendedImageModel) 和备份 (T=ExtendedImageModel[])。
 *
 * @example
 * model_create: modelRoutes({
 *   '4K': {
 *     '1x1,16x9,9x16,4x3,3x4': kie.image('gpt-image-2'),
 *     'other': blt.image('gpt-image-2-official-1'),
 *   },
 *   'other': { 'other': blt.image('gpt-image-2-official-1') },
 * }, blt.image('gpt-image-2-official-1'))
 *
 * modelBackup_create: modelRoutes({
 *   '4K':   { 'other': [blt.image('x')] },
 *   '1K,2K': {
 *     '1x1,16x9': [blt.image('x'), wavespeed.image('y'), kie.image('z')],
 *     'other':    [blt.image('x'), wavespeed.image('y')],
 *   },
 * }, [blt.image('x')])
 */
export function modelRoutes<T>(
  routes: Record<string, Record<string, T>>,
  fallback: T,
): (ctx: BackupResolverContext) => T {
  const expanded = new Map<string, Map<string, T>>();
  for (const [resKey, sizeMap] of Object.entries(routes)) {
    const inner = new Map<string, T>();
    for (const [sizeKey, val] of Object.entries(sizeMap)) {
      for (const sk of sizeKey.split(',')) inner.set(sk.trim(), val);
    }
    for (const rk of resKey.split(',')) expanded.set(rk.trim(), inner);
  }
  return ({ resolution, size }) => {
    const resMatch = (resolution && expanded.get(resolution)) || expanded.get('other');
    if (!resMatch) return fallback;
    return (size && resMatch.get(size)) || resMatch.get('other') || fallback;
  };
}

type PixelSize = `${number}x${number}`
export type ImageSize = PixelSize | 'match';

/** 图片节点的模式（对应节点数据 imageMode）：t2i 文生图 / i2i 图生图 */
export type ImageModeKey = 't2i' | 'i2i';

export type ImageResolution = '1K' | '2K' | '3K' | '4K' | '8K' | '1x' | '2x' | '4x' | '8x';

/** 按模式可覆盖的配置（仿照 video 的 configByMode），未填字段回退到模型顶层配置 */
export type ImageModeConfig = {
  sizes?: ImageSize[];
  sizeNotes?: Record<string, string>;
  resolutions?: ImageResolution[];
  resolutionConstraints?: Record<string, string[]>;
  imagesCountOptions?: number[];
  inlineMedia?: {
    [K in keyof InlineMediaConfig]?: InlineMediaConfig[K] extends (infer U | undefined)
      ? (U extends object ? Partial<U> : U) | undefined
      : Partial<InlineMediaConfig[K]>;
  };
};

export type TersaImageModel = TersaModel & {
  providers: (TersaProvider & {
    model?: ExtendedImageModel;
    // 可选：为不同场景单独指定模型（不填则回退到 model）
    model_create?: PrimaryModelOption;
    model_edit?: PrimaryModelOption;
    // 备选模型：通用与场景化（不填则回退到通用或空数组）
    modelBackup?: BackupList;
    modelBackup_create?: BackupList;
    modelBackup_edit?: BackupList;
    getCost: (props?: {
      textInput?: number;
      imageInput?: number;
      output?: number;
      size?: string;
      mode?: ImageModeKey;
      providerOptions?: Record<string, any>;
    }) => number;
    getPassCount?: (props?: {
      size?: string;
      mode?: ImageModeKey;
      providerOptions?: Record<string, any>;
    }) => number;
  })[];
  // 尺寸（可省略：全部放在 configByMode 时不需要顶层兜底）
  sizes?: ImageSize[];
  sizeNotes?: Record<string, string>;
  // 分辨率选项（若存在，则前端显示分辨率选择器并使用该列表）
  // 说明：
  // - '1K'...'8K'：传统"分辨率档位"（用于多数生图模型）
  // - '1x'...'8x'：超分"倍率档位"（用于 upscale / super-resolution 模型，如 Topaz）
  resolutions?: ImageResolution[];
  // 尺寸→分辨率约束：某些极端比例只允许特定分辨率（key 为 size 值，value 为允许的分辨率列表）
  resolutionConstraints?: Record<string, string[]>;
  // time
  // precheck（可省略：只配 precheckByMode 时不需要顶层兜底）
  precheck?: string[];
  /** 按模式（t2i / i2i）单独指定 precheck，未命中时回退到顶层 precheck */
  precheckByMode?: Partial<Record<ImageModeKey, string[]>>;
  /** 按模式（t2i / i2i）覆盖 sizes/resolutions/imagesCountOptions/inlineMedia 等配置，未填字段回退到顶层 */
  configByMode?: Partial<Record<ImageModeKey, ImageModeConfig>>;
  // 支持输入
  supportsInput: string[];
  // 图生图相关
  supportsEdit: boolean;
  supportsImageType: 'url' | 'base64';
  // 单次生成允许的数量列表（用于前端 UI 与后端参数约束）
  imagesCountOptions?: number[];
  // 是否在请求体中传递生成数量参数（与 AI SDK 的 n 参数互斥）
  useRequestBodyForCount?: boolean;
  /**
   * UI-only: hide the node's instruction/prompt input row (toolbar becomes 2 rows).
   */
  hideInstructions?: boolean;
  /**
   * UI-only: enable plugins for the Image Node toolbar.
   */
  plugin?: ImagePluginId[];
  // 额外配置
  image2imageUrl?: string;
  text2imageUrl?: string;
  providerOptions?: Record<string, Record<string, string>>;
  // 价格
  priceIndicator: 'lowest' | 'low' | 'high' | 'highest';
  price: string;
  tags?: string[];
  /** 自定义备注，显示在卡片下方 */
  note?: string;
  /** Inline media upload configuration (images uploaded directly to the node) */
  inlineMedia?: InlineMediaConfig;
  /** Inngest 轮询最大尝试次数（可选，默认 50 ≈ 20min） */
  maxPollAttempts?: number;
};

/**
 * 解析图片节点的有效模式：优先节点数据 imageMode，
 * 未设置时按模型能力兜底（与节点初始化 effect 的 fallback 一致：优先 i2i）。
 */
export function resolveImageMode(
  model: Pick<TersaImageModel, 'supportsInput' | 'supportsEdit'> | null | undefined,
  imageMode?: string | null,
): ImageModeKey {
  if (imageMode === 't2i' || imageMode === 'i2i') return imageMode;
  const si = Array.isArray(model?.supportsInput) ? model.supportsInput : [];
  return si.includes('i2i') || model?.supportsEdit ? 'i2i' : 't2i';
}

export type EffectiveImageConfig = {
  sizes: ImageSize[];
  sizeNotes?: Record<string, string>;
  resolutions?: ImageResolution[];
  resolutionConstraints?: Record<string, string[]>;
  imagesCountOptions?: number[];
  inlineMedia?: InlineMediaConfig;
};

/**
 * 按当前模式合并模型配置（仿照 video transform 的 getEffectiveConfig）：
 * configByMode[mode] 中的字段覆盖顶层，inlineMedia 深度合并。
 */
export function getEffectiveImageConfig(
  model: TersaImageModel | null | undefined,
  imageMode?: string | null,
): EffectiveImageConfig {
  const base: EffectiveImageConfig = {
    sizes: model?.sizes ?? [],
    sizeNotes: model?.sizeNotes,
    resolutions: model?.resolutions,
    resolutionConstraints: model?.resolutionConstraints,
    imagesCountOptions: model?.imagesCountOptions,
    inlineMedia: model?.inlineMedia,
  };
  if (!model?.configByMode) return base;
  const mode = resolveImageMode(model, imageMode);
  const override = model.configByMode[mode];
  if (!override) return base;
  return {
    sizes: override.sizes ?? base.sizes,
    sizeNotes: override.sizeNotes ?? base.sizeNotes,
    resolutions: override.resolutions ?? base.resolutions,
    resolutionConstraints: override.resolutionConstraints ?? base.resolutionConstraints,
    imagesCountOptions: override.imagesCountOptions ?? base.imagesCountOptions,
    inlineMedia: mergeInlineMedia(base.inlineMedia, override.inlineMedia),
  };
}

/** 按当前模式取 precheck：precheckByMode[mode] 优先，未命中回退到顶层 precheck */
export function getEffectiveImagePrecheck(
  model: TersaImageModel | null | undefined,
  imageMode?: string | null,
): string[] {
  if (!model) return [];
  const mode = resolveImageMode(model, imageMode);
  const byMode = model.precheckByMode?.[mode];
  return Array.isArray(byMode) ? byMode : (model.precheck ?? []);
}
